import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Select } from '@/components/ui';
import {
  getCaseSettings,
  openDisciplineCase,
  type HrCase,
} from '@/api/services/hrCases.service';
import type { HrPersonRef } from '@/api/services/hrWorkspace.service';
import { EmployeePicker } from '../people/EmployeePicker';
import type { HrAreaProps } from '../hrShared';
import { FormShell, TextArea } from './caseFields';
import { useCaseCommand } from './useCaseCommand';

/**
 * Opens a disciplinary case. The opener becomes its investigator; the
 * summary is visible to the case team only. Case types belong to a company,
 * so they load once the employee is chosen.
 */
export function OpenDisciplineDialog({
  companyIds,
  showCompany,
  onOpened,
  onClose,
}: HrAreaProps & {
  onOpened: (opened: { id: string; companyId: string }) => void;
  onClose: () => void;
}): React.ReactElement {
  const [person, setPerson] = useState<HrPersonRef | null>(null);
  const companyId = person?.companyId ?? '';
  const [typeKey, setTypeKey] = useState('');
  const [summary, setSummary] = useState('');
  const { data: settings } = useQuery({
    queryKey: ['hr-case-settings', companyId],
    queryFn: () => getCaseSettings(companyId),
    enabled: companyId !== '',
  });
  const activeTypes = (settings?.disciplineCaseTypes ?? []).filter((type) => type.active);
  const chosenType = typeKey || activeTypes[0]?.key || '';
  const command = useCaseCommand(
    companyId,
    null,
    (requestId, input: { subjectUserId: string; typeKey: string; summary: string }) =>
      openDisciplineCase(companyId, { requestId, ...input }),
    'Case opened',
    (opened: HrCase) => onOpened({ id: opened._id, companyId })
  );
  const ready = person !== null && chosenType !== '' && summary.trim() !== '';

  return (
    <Modal open onClose={onClose} title="Open disciplinary case" className="max-w-xl">
      <FormShell
        title="Case details"
        error={command.error}
        onCancel={onClose}
        onSubmit={() =>
          command.submit({
            subjectUserId: person?.userId ?? '',
            typeKey: chosenType,
            summary: summary.trim(),
          })
        }
        submitLabel="Open case"
        disabled={!ready}
        isPending={command.isPending}
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Only the case team can see this case. You join it as the investigator; add a decision
          maker before a decision is due.
        </p>
        <EmployeePicker
          id="open-case-subject"
          companyIds={companyIds}
          showCompany={showCompany}
          value={person}
          onChange={(next) => {
            setPerson(next);
            // Case types belong to the person's company.
            setTypeKey('');
          }}
        />
        <Select
          id="open-case-type"
          label="Case type"
          value={chosenType}
          onChange={setTypeKey}
          disabled={!person}
          placeholder={person ? 'Choose a type' : 'Choose an employee first'}
          options={activeTypes.map((type) => ({ value: type.key, label: type.label }))}
          clearable={false}
        />
        <TextArea
          id="open-case-summary"
          label="Allegation summary"
          value={summary}
          onChange={setSummary}
          required
          rows={4}
          hint="Case team only. What the employee is told is written separately, when a hearing is set."
        />
      </FormShell>
    </Modal>
  );
}
